import React from "react";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { IMaskInput } from "react-imask";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { FormHelperText } from "@mui/material";
import classes from "./InputText.module.scss";

// Styled TextField
const StyledTextField = styled(TextField)(({ theme }) => ({
  "label + &": {
    marginTop: theme.spacing(2),
  },
  "& .MuiInputBase-input": {
    borderRadius: 4,
    backgroundColor: "#FFF",
    border: "1px solid",
    borderColor: theme.palette.mode === "light" ? "#E0E3E7" : "#2D3843",
    width: "100%",
    padding: "6px 12px",
    transition: theme.transitions.create([
      "border-color",
      "background-color",
      "box-shadow",
    ]),
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
  },
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": {
      borderColor: theme.palette.mode === "light" ? "#E0E3E7" : "#2D3843",
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main, // Ajuste para o tema principal
    },
  },
}));

// Masked TextField Component
const MaskedTextField = React.forwardRef(function MaskedTextField(props, ref) {
  const { mask, maskOptions = {}, onChange, ...other } = props;

  return (
    <IMaskInput
      {...other}
      mask={mask}
      inputRef={ref}
      {...maskOptions}
      onAccept={(value) => {
        if (onChange) {
          onChange({ target: { name: props.name, value } });
        }
      }}
      overwrite
    />
  );
});

// Input Field Component
const InputField = ({
  label,
  error,
  errorText,
  placeholder,
  mask,
  maskOptions,
  name,
  value,
  onChange,
  onBlur,
  onKeyDown,
  className,
  required,
  helperText,
  ...rest
}) => {

  return (
    <>
      {label && (
        <InputLabel
          required={required}
          style={{
            color: "#4D4D4D",
            fontWeight: "700"
          }}
          shrink
          htmlFor={name}
        >
          {label}
        </InputLabel>
      )}
      <StyledTextField
        error={error}
        placeholder={placeholder}
        name={name}
        value={value}
        helperText={helperText}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        InputProps={{
          inputComponent: mask ? MaskedTextField : undefined,
          inputProps: {
            ...(mask ? { mask, maskOptions } : {}),
            ...rest,
          },
        }}
        variant="outlined"
        size="small"
        fullWidth
        className={className} // Certifique-se de propagar a classe para personalização
      />
    </>
  );
};

// InputText Component
const InputText = ({
  label,
  placeholder,
  required = false,
  error = null,
  errorText = "",
  onChange,
  onBlur,
  onKeyDown,
  value,
  name,
  mask,
  maskOptions = {},
  doubleInput = false,
  valuePrimary,
  valueSecondary,
  maskPrimary,
  maskSecondary,
  namePrimary,
  nameSecondary,
  placeholderPrimary,
  placeholderSecondary,
  errorPrimary,
  small = null,

  errorSecondary,
  errorTextPrimary,
  errorTextSecondary,
  onChangePrimary,
  onChangeSecondary,
  classPrimary = classes.FromControlNormal,
  classSecondary = classes.FromControlNormal,
  ...rest
}) => {
  const showError = error && <FormHelperText error>{errorText || errorTextPrimary}</FormHelperText>;
  const showErrorSecundary = error && <FormHelperText error>{errorTextSecondary}</FormHelperText>;

  return (
    <>
      {!doubleInput && (
        <FormControl
          variant="standard"
          className={
            small ? classes.FromControlFifty : classes.FromControlNormal
          }
        >
          <InputField
            label={label}
            error={error}
            errorText={errorText}
            placeholder={placeholder}
            mask={mask}
            maskOptions={maskOptions}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            className={classPrimary}
            {...rest}
          />
          {showError}
        </FormControl>
      )}
      {doubleInput && (
        <FormControl
          variant="standard"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div
            style={{ display: "flex", alignItems: "baseline", gap: "0 10px" }}
          >
            <InputField
              label={label}
              error={errorPrimary}
              helperText={errorPrimary && errorTextPrimary}
              placeholder={placeholderPrimary}
              mask={maskPrimary}
              maskOptions={maskOptions}
              name={namePrimary}
              variant="outlined"
              value={valuePrimary}
              onChange={onChangePrimary}
              className={classes.InputLabel}
              {...rest}
            />
            <InputField
              errorText={errorTextSecondary}
              helperText={errorSecondary && errorTextSecondary}
              error={errorSecondary}

              placeholder={placeholderSecondary}
              mask={maskSecondary}
              maskOptions={maskOptions}
              name={nameSecondary}
              style={{ marginLeft: "10px" }}
              variant="outlined"
              value={valueSecondary}
              onChange={onChangeSecondary}
              className={classPrimary}
              {...rest}
            />
          </div>
        </FormControl>
      )}
    </>
  );
};

export default InputText;
